import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Users,
  Activity,
  GraduationCap,
  Calendar,
  FileCheck,
  ShieldCheck,
  Search,
  RefreshCw,
  UserPlus,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Clock,
  Globe,
  Monitor,
  Smartphone,
  LogOut,
  Download,
  Copy,
  Check,
  X,
  FileText,
  ChevronRight,
  Award
} from 'lucide-react';
import DashboardLayout from './components/layouts/DashboardLayout';
import ApprovalStatusHistory from './dashboards/website-admin/ApprovalStatusHistory';
import AdmissionsExecutiveOverview from './dashboards/principal/AdmissionsExecutiveOverview';
import './styles/dashboard.css';
import './dashboards/super-admin/SuperAdminPortal.css';

const ROLE_OPTIONS = [
  {
    code: 'admin',
    label: 'Website Admin (CMS)',
    desc: 'Mengelola seluruh konten halaman website dan testimoni.'
  },
  {
    code: 'approver',
    label: 'Principal (Approver)',
    desc: 'Meninjau, menyetujui (Approve), atau menolak (Reject) perubahan konten.'
  },
  {
    code: 'admissions',
    label: 'Admin Admissions (PPDB)',
    desc: 'Mengelola data pendaftaran siswa baru dan hasil seleksi.'
  },
  {
    code: 'super_admin',
    label: 'Super Admin',
    desc: 'Otoritas penuh pengelolaan akun user, keamanan, dan pemantauan sistem.'
  }
];

function getRoleBadgeInfo(roleCode) {
  const code = String(roleCode || '').toLowerCase().trim();
  if (code === 'super_admin') {
    return { label: 'Super Admin', cls: 'sa-badge-role-super_admin' };
  }
  if (code === 'director' || code === 'school_director') {
    return { label: 'Direktur Sekolah (Director)', cls: 'sa-badge-role-director' };
  }
  if (code === 'admin' || code === 'website_admin') {
    return { label: 'Website Admin', cls: 'sa-badge-role-admin' };
  }
  if (code === 'approver' || code === 'principal') {
    return { label: 'Principal', cls: 'sa-badge-role-approver' };
  }
  if (code === 'admissions') {
    return { label: 'Admin Admissions', cls: 'sa-badge-role-admissions' };
  }
  return { label: roleCode || 'Staff', cls: 'sa-badge-offline' };
}

function parseUserAgentInfo(uaString) {
  if (!uaString || typeof uaString !== 'string') {
    return { browser: 'Web Browser', os: 'Desktop / Mobile', isMobile: false };
  }
  const ua = uaString.toLowerCase();
  let browser = 'Browser';
  if (ua.includes('edg/')) browser = 'Microsoft Edge';
  else if (ua.includes('opr/') || ua.includes('opera')) browser = 'Opera';
  else if (ua.includes('chrome/') && !ua.includes('edg/')) browser = 'Google Chrome';
  else if (ua.includes('firefox/')) browser = 'Mozilla Firefox';
  else if (ua.includes('safari/') && !ua.includes('chrome/')) browser = 'Apple Safari';

  let os = 'Unknown OS';
  if (ua.includes('windows nt 10.0')) os = 'Windows 10/11';
  else if (ua.includes('windows')) os = 'Windows';
  else if (ua.includes('mac os x') && !ua.includes('iphone') && !ua.includes('ipad')) os = 'macOS';
  else if (ua.includes('android')) os = 'Android';
  else if (ua.includes('iphone') || ua.includes('ipad')) os = 'iOS';
  else if (ua.includes('linux')) os = 'Linux';

  const isMobile = ua.includes('mobile') || ua.includes('android') || ua.includes('iphone');
  return { browser, os, isMobile };
}

function formatRelativeTime(dateStr, secondsAgo = null) {
  if (secondsAgo !== null && secondsAgo !== undefined && !Number.isNaN(Number(secondsAgo))) {
    const sec = Math.max(0, Number(secondsAgo));
    if (sec < 60) return 'Baru saja';
    if (sec < 3600) return `${Math.floor(sec / 60)} menit lalu`;
    if (sec < 86400) return `${Math.floor(sec / 3600)} jam lalu`;
    return `${Math.floor(sec / 86400)} hari lalu`;
  }
  if (!dateStr) return '-';
  const d = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return dateStr;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'Baru saja';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} menit lalu`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} jam lalu`;
  return d.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(String(dateStr).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getAdmissionStatusBadge(statusCode) {
  const s = String(statusCode || 'submitted').toLowerCase();
  if (s === 'approved' || s === 'passed') {
    return { label: 'Disetujui (Approved)', cls: 'sa-badge-status-approved' };
  }
  if (s === 'in_review' || s === 'pending') {
    return { label: 'Sedang Direview', cls: 'sa-badge-status-in_review' };
  }
  if (s === 'rejected' || s === 'failed') {
    return { label: 'Ditolak (Rejected)', cls: 'sa-badge-status-rejected' };
  }
  if (s === 'revision_required') {
    return { label: 'Perlu Revisi', cls: 'sa-badge-status-revision_required' };
  }
  return { label: 'Terdaftar (Submitted)', cls: 'sa-badge-status-submitted' };
}

function getLevelDisplayName(levelCode) {
  const code = String(levelCode || '').toUpperCase();
  const map = {
    PG: 'Playgroup & Preschool (PG)',
    TK: 'Kindergarten (TK)',
    SD: 'Primary School (SD)',
    SMP: 'Secondary School (SMP)',
    SMA: 'Senior High School (SMA)'
  };
  return map[code] || code || '-';
}

function exportRowsToCsv(filename, headers, rows) {
  const escapeCell = (val) => {
    const s = val === null || val === undefined ? '' : String(val);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const csvContent = [
    headers.map(escapeCell).join(','),
    ...rows.map((r) => r.map(escapeCell).join(','))
  ].join('\r\n');

  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function SuperAdminDashboard({ user, onLogout, apiBase }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [toast, setToast] = useState(null);

  // Core data states
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const [sessionData, setSessionData] = useState({
    online_count: 1,
    online_users: [],
    all_user_sessions: [],
    recent_login_logs: [],
    requester_ip: '-'
  });
  const [sessionsLoading, setSessionsLoading] = useState(true);

  const [applications, setApplications] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [applicantsLoading, setApplicantsLoading] = useState(true);

  const [cmsCounts, setCmsCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [auditEvents, setAuditEvents] = useState([]);
  const [contentHistory, setContentHistory] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  // User Management filters & modal state
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    full_name: '',
    email: '',
    password: '',
    role_code: 'admin',
    is_active: true
  });
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [userSubmitting, setUserSubmitting] = useState(false);
  const [userFormError, setUserFormError] = useState('');

  // Active Users sub-tab & copy state
  const [sessionSubTab, setSessionSubTab] = useState('online');
  const [copiedIp, setCopiedIp] = useState('');

  // Applicants filters, sub-tab & modal state
  const [applicantSubTab, setApplicantSubTab] = useState('applications');
  const [appSearch, setAppSearch] = useState('');
  const [appLevelFilter, setAppLevelFilter] = useState('all');
  const [appStatusFilter, setAppStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [appModalStatus, setAppModalStatus] = useState('submitted');
  const [appModalNote, setAppModalNote] = useState('');
  const [appUpdating, setAppUpdating] = useState(false);

  // Audit Log sub-tab & filters
  const [auditSubTab, setAuditSubTab] = useState('security');
  const [auditSearch, setAuditSearch] = useState('');
  const [auditTypeFilter, setAuditTypeFilter] = useState('all');

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  }, []);

  // Master Signatories State (Point 7)
  const [signatoriesList, setSignatoriesList] = useState([]);
  const [globalFinanceName, setGlobalFinanceName] = useState('Zahra, S.E.');
  const [globalFinanceTitle, setGlobalFinanceTitle] = useState('Head of Finance & Administration');
  const [signatoriesLoading, setSignatoriesLoading] = useState(false);
  const [savingSignatories, setSavingSignatories] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token =
      localStorage.getItem('ph_auth_token') ||
      localStorage.getItem('auth_token') ||
      sessionStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, []);

  // 1. Load Users
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/users?_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const json = await res.json();
        if (json.ok && Array.isArray(json.data)) {
          setUsers(json.data);
        }
      }
    } catch (err) {
      console.error('[SuperAdmin] Failed to load users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  // 2. Load Active Sessions & IP Info
  const loadActiveSessions = useCallback(async (silent = false) => {
    if (!silent) setSessionsLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/active-sessions?_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const json = await res.json();
        if (json.ok && json.data) {
          setSessionData({
            online_count: json.data.online_count ?? (json.data.online_users?.length || 1),
            online_users: Array.isArray(json.data.online_users) ? json.data.online_users : [],
            all_user_sessions: Array.isArray(json.data.all_user_sessions) ? json.data.all_user_sessions : [],
            recent_login_logs: Array.isArray(json.data.recent_login_logs) ? json.data.recent_login_logs : [],
            requester_ip: json.data.requester_ip || '-'
          });
          return;
        }
      }

      // Fallback if /admin/active-sessions is not yet deployed on production server
      if (user) {
        const fallbackSelf = {
          id: user.id || 1,
          full_name: user.full_name || 'Super Admin',
          email: user.email || '',
          is_active: true,
          role_codes: user.roles || user.role_codes || ['super_admin'],
          ip_address: 'Sesi Aktif Saat Ini',
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          is_online: true,
          login_at: new Date().toISOString(),
          last_active_at: new Date().toISOString(),
          seconds_since_active: 0,
          is_current_session: true
        };
        setSessionData((prev) => ({
          ...prev,
          online_count: Math.max(1, prev.online_users.length),
          online_users: prev.online_users.length > 0 ? prev.online_users : [fallbackSelf]
        }));
      }
    } catch (err) {
      console.warn('[SuperAdmin] Active sessions warning:', err);
    } finally {
      if (!silent) setSessionsLoading(false);
    }
  }, [apiBase, getAuthHeaders, user]);

  // 3. Load Admissions Applications & Portal Users
  const loadApplicantsData = useCallback(async () => {
    setApplicantsLoading(true);
    try {
      const headers = getAuthHeaders();
      const [appsRes, portalRes] = await Promise.allSettled([
        fetch(`${apiBase}/admin/admissions/applications?_t=${Date.now()}`, {
          credentials: 'include',
          headers
        }),
        fetch(`${apiBase}/admin/admissions/portal-users?_t=${Date.now()}`, {
          credentials: 'include',
          headers
        })
      ]);

      if (appsRes.status === 'fulfilled' && appsRes.value.ok) {
        const appsJson = await appsRes.value.json();
        if (appsJson.ok && Array.isArray(appsJson.data)) {
          const normalized = appsJson.data.map((item) => {
            let formData = item.form_data || {};
            if (typeof item.form_payload === 'string' && Object.keys(formData).length === 0) {
              try {
                formData = JSON.parse(item.form_payload) || {};
              } catch (_) {}
            }
            return {
              ...item,
              form_data: formData,
              student_name:
                item.student_name ||
                formData.student_full_name ||
                formData.student_name ||
                item.applicant_name ||
                '-',
              birth_date: item.birth_date || formData.birth_date || '-',
              parent_name: item.parent_name || formData.parent_name || item.applicant_name || '-',
              phone: item.phone || formData.phone || item.applicant_phone || '-'
            };
          });
          setApplications(normalized);
        }
      }

      if (portalRes.status === 'fulfilled' && portalRes.value.ok) {
        const portalJson = await portalRes.value.json();
        if (portalJson.ok && Array.isArray(portalJson.data)) {
          setPortalUsers(portalJson.data);
        }
      }
    } catch (err) {
      console.error('[SuperAdmin] Failed to load applicants data:', err);
    } finally {
      setApplicantsLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  // 4. Load CMS Submissions Counts & Audit Logs
  const loadSystemLogsAndCms = useCallback(async () => {
    setAuditLoading(true);
    try {
      const headers = getAuthHeaders();
      const [faRes, accessRes, historyRes] = await Promise.allSettled([
        fetch(`${apiBase}/homepage/cms/field-approvals?status=all&limit=200&_t=${Date.now()}`, {
          credentials: 'include',
          headers
        }),
        fetch(`${apiBase}/admin/access-history?_t=${Date.now()}`, {
          credentials: 'include',
          headers
        }),
        fetch(`${apiBase}/admin/update-history?_t=${Date.now()}`, {
          credentials: 'include',
          headers
        })
      ]);

      let cmsLoaded = false;
      if (faRes.status === 'fulfilled' && faRes.value.ok) {
        const faJson = await faRes.value.json().catch(() => null);
        if (faJson) {
          if (faJson.status_counts) {
            setCmsCounts({
              pending: Number(faJson.status_counts.pending || 0),
              approved: Number(faJson.status_counts.approved || 0),
              rejected: Number(faJson.status_counts.rejected || 0)
            });
            cmsLoaded = true;
          } else if (Array.isArray(faJson.data)) {
            setCmsCounts({
              pending: faJson.data.filter((x) => x.status === 'pending').length,
              approved: faJson.data.filter((x) => x.status === 'approved').length,
              rejected: faJson.data.filter((x) => x.status === 'rejected').length
            });
            cmsLoaded = true;
          }
        }
      }

      if (!cmsLoaded) {
        const fallbackCmsUrls = [
          `${apiBase}/admin/cms-submissions?status=all&limit=200&_t=${Date.now()}`,
          `${apiBase}/submissions/all?status=all&limit=200&_t=${Date.now()}`,
          `${apiBase}/submissions/pending?limit=200&_t=${Date.now()}`
        ];
        for (const fbUrl of fallbackCmsUrls) {
          try {
            const fbRes = await fetch(fbUrl, { credentials: 'include', headers });
            if (!fbRes.ok) continue;
            const fbJson = await fbRes.json().catch(() => null);
            if (!fbJson) continue;
            if (fbJson.status_counts) {
              setCmsCounts({
                pending: Number(fbJson.status_counts.pending || 0),
                approved: Number(fbJson.status_counts.approved || 0),
                rejected: Number(fbJson.status_counts.rejected || 0)
              });
              break;
            } else if (Array.isArray(fbJson.data)) {
              setCmsCounts({
                pending: fbJson.data.filter((x) => (x.status || 'pending') === 'pending').length,
                approved: fbJson.data.filter((x) => x.status === 'approved').length,
                rejected: fbJson.data.filter((x) => x.status === 'rejected').length
              });
              break;
            }
          } catch (_) {}
        }
      }

      if (accessRes.status === 'fulfilled' && accessRes.value.ok) {
        const accJson = await accessRes.value.json();
        if (accJson.ok && Array.isArray(accJson.data?.events)) {
          setAuditEvents(accJson.data.events);
        }
      }

      if (historyRes.status === 'fulfilled' && historyRes.value.ok) {
        const histJson = await historyRes.value.json();
        if (histJson.ok && Array.isArray(histJson.data)) {
          setContentHistory(histJson.data);
        }
      }
    } catch (err) {
      console.warn('[SuperAdmin] System logs warning:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  const loadSignatories = useCallback(async () => {
    setSignatoriesLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/signatories?_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.ok && Array.isArray(data.data)) {
        setSignatoriesList(data.data);
        if (data.data.length > 0 && data.data[0].finance_pic_name) {
          setGlobalFinanceName(data.data[0].finance_pic_name);
          setGlobalFinanceTitle(data.data[0].finance_pic_title || 'Head of Finance & Administration');
        }
      }
    } catch (err) {
      console.error('Failed to load signatories:', err);
    } finally {
      setSignatoriesLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  const handleSaveSignatories = async (e) => {
    e.preventDefault();
    setSavingSignatories(true);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/signatories`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          signatories: signatoriesList,
          finance_pic_name: globalFinanceName,
          finance_pic_title: globalFinanceTitle,
        }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.ok) {
        showToast(data.message || 'Data Pejabat Penandatangan Dokumen berhasil disimpan!', 'success');
        await loadSignatories();
      } else {
        showToast(data?.message || 'Gagal menyimpan data pejabat penandatangan', 'error');
      }
    } catch (_) {
      showToast('Terjadi kesalahan jaringan saat menyimpan data', 'error');
    } finally {
      setSavingSignatories(false);
    }
  };

  const refreshAllData = useCallback(() => {
    loadUsers();
    loadActiveSessions(false);
    loadApplicantsData();
    loadSystemLogsAndCms();
    loadSignatories();
  }, [loadUsers, loadActiveSessions, loadApplicantsData, loadSystemLogsAndCms, loadSignatories]);

  useEffect(() => {
    refreshAllData();
  }, [refreshAllData]);

  useEffect(() => {
    if (activeTab === 'signatories') {
      loadSignatories();
    }
  }, [activeTab, loadSignatories]);

  // Live poll active sessions every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadActiveSessions(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [loadActiveSessions]);

  // User Modal Handlers
  const openCreateUserModal = () => {
    setEditingUser(null);
    setUserForm({
      full_name: '',
      email: '',
      password: '',
      role_code: 'admin',
      is_active: true
    });
    setShowPasswordText(false);
    setUserFormError('');
    setShowUserModal(true);
  };

  const openEditUserModal = (targetUser) => {
    setEditingUser(targetUser);
    const primaryRole =
      Array.isArray(targetUser.role_codes) && targetUser.role_codes.length > 0
        ? targetUser.role_codes[0]
        : 'admin';
    setUserForm({
      full_name: targetUser.full_name || '',
      email: targetUser.email || '',
      password: '',
      role_code: primaryRole,
      is_active: Boolean(targetUser.is_active)
    });
    setShowPasswordText(false);
    setUserFormError('');
    setShowUserModal(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setUserFormError('');

    if (!userForm.full_name.trim()) {
      setUserFormError('Nama lengkap wajib diisi.');
      return;
    }
    if (!userForm.email.trim()) {
      setUserFormError('Alamat email wajib diisi.');
      return;
    }
    if (!editingUser && !userForm.password) {
      setUserFormError('Password wajib diisi untuk user baru.');
      return;
    }
    if (userForm.password && userForm.password.length < 8) {
      setUserFormError('Password minimal harus 8 karakter.');
      return;
    }
    if (!userForm.role_code) {
      setUserFormError('Pilih minimal satu role.');
      return;
    }

    setUserSubmitting(true);
    try {
      const isEdit = Boolean(editingUser);
      const url = isEdit ? `${apiBase}/admin/users/${editingUser.id}` : `${apiBase}/admin/users`;
      const method = isEdit ? 'PUT' : 'POST';

      const payload = {
        full_name: userForm.full_name.trim(),
        email: userForm.email.trim().toLowerCase(),
        is_active: Boolean(userForm.is_active),
        role_codes: [userForm.role_code]
      };
      if (userForm.password) {
        payload.password = userForm.password;
      }

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.message || 'Gagal menyimpan data user.');
      }

      showToast(isEdit ? 'Data user berhasil diperbarui.' : 'User baru berhasil ditambahkan.');
      setShowUserModal(false);
      loadUsers();
      loadActiveSessions(true);
      loadSystemLogsAndCms();
    } catch (err) {
      setUserFormError(err.message || 'Terjadi kesalahan saat menyimpan user.');
    } finally {
      setUserSubmitting(false);
    }
  };

  const handleQuickToggleUserActive = async (targetUser) => {
    if (user && Number(targetUser.id) === Number(user.id)) {
      showToast('Anda tidak dapat menonaktifkan akun Anda sendiri.', 'error');
      return;
    }
    const nextStatus = !targetUser.is_active;
    try {
      const res = await fetch(`${apiBase}/admin/users/${targetUser.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          full_name: targetUser.full_name,
          is_active: nextStatus,
          role_codes: targetUser.role_codes?.length ? targetUser.role_codes : ['admin']
        })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.message || 'Gagal mengubah status user.');
      }
      showToast(`Akun ${targetUser.full_name} berhasil ${nextStatus ? 'diaktifkan' : 'dinonaktifkan'}.`);
      loadUsers();
      loadActiveSessions(true);
    } catch (err) {
      showToast(err.message || 'Gagal mengubah status user.', 'error');
    }
  };

  const handleDeleteUser = async (targetUser) => {
    if (user && Number(targetUser.id) === Number(user.id)) {
      showToast('Anda tidak dapat menghapus akun Anda sendiri.', 'error');
      return;
    }
    if (!window.confirm(`Apakah Anda yakin ingin menghapus akun "${targetUser.full_name}" (${targetUser.email})?`)) {
      return;
    }
    try {
      const res = await fetch(`${apiBase}/admin/users/${targetUser.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.message || 'Gagal menghapus user.');
      }
      showToast(`User ${targetUser.full_name} berhasil dihapus.`);
      loadUsers();
      loadActiveSessions(true);
      loadSystemLogsAndCms();
    } catch (err) {
      showToast(err.message || 'Gagal menghapus user.', 'error');
    }
  };

  const handleRevokeSession = async (sessionItem) => {
    if (sessionItem.is_current_session || (user && Number(sessionItem.id) === Number(user.id))) {
      showToast('Tidak dapat mengakhiri sesi Anda sendiri dari menu ini.', 'error');
      return;
    }
    if (!window.confirm(`Akhiri paksa sesi aktif milik "${sessionItem.full_name}"?`)) {
      return;
    }
    try {
      const res = await fetch(`${apiBase}/admin/active-sessions/revoke`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: sessionItem.id })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.message || 'Gagal mengakhiri sesi.');
      }
      showToast(`Sesi aktif ${sessionItem.full_name} berhasil diakhiri.`);
      loadActiveSessions(false);
      loadUsers();
    } catch (err) {
      showToast(err.message || 'Gagal mengakhiri sesi.', 'error');
    }
  };

  const handleCopyIp = (ip) => {
    if (!ip || ip === '-') return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(ip);
      setCopiedIp(ip);
      setTimeout(() => setCopiedIp(''), 2000);
    }
  };

  const openApplicationDetailModal = (app) => {
    setSelectedApplication(app);
    setAppModalStatus(app.status_code || 'submitted');
    setAppModalNote(app.admin_note || '');
  };

  const handleUpdateApplicationStatus = async (e) => {
    e.preventDefault();
    if (!selectedApplication) return;
    setAppUpdating(true);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/applications/${selectedApplication.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          status_code: appModalStatus,
          admin_note: appModalNote
        })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.message || 'Gagal memperbarui status pendaftaran.');
      }
      showToast(`Status pendaftaran #${selectedApplication.id} berhasil diperbarui.`);
      setSelectedApplication(null);
      loadApplicantsData();
    } catch (err) {
      showToast(err.message || 'Gagal memperbarui status pendaftaran.', 'error');
    } finally {
      setAppUpdating(false);
    }
  };

  // Computed metrics & filtered lists
  const sessionMapByUserId = React.useMemo(() => {
    const map = {};
    (sessionData.all_user_sessions || []).forEach((s) => {
      map[Number(s.id)] = s;
    });
    return map;
  }, [sessionData.all_user_sessions]);

  const filteredUsers = users.filter((u) => {
    const q = userSearch.trim().toLowerCase();
    const matchesSearch =
      !q ||
      String(u.full_name || '').toLowerCase().includes(q) ||
      String(u.email || '').toLowerCase().includes(q);
    const roles = Array.isArray(u.role_codes) ? u.role_codes : [];
    const matchesRole = userRoleFilter === 'all' || roles.includes(userRoleFilter);
    const matchesStatus =
      userStatusFilter === 'all' ||
      (userStatusFilter === 'active' && u.is_active) ||
      (userStatusFilter === 'inactive' && !u.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const filteredApplications = applications.filter((app) => {
    const q = appSearch.trim().toLowerCase();
    const matchesSearch =
      !q ||
      String(app.student_name || '').toLowerCase().includes(q) ||
      String(app.parent_name || '').toLowerCase().includes(q) ||
      String(app.applicant_email || '').toLowerCase().includes(q) ||
      String(app.phone || '').toLowerCase().includes(q);
    const matchesLevel =
      appLevelFilter === 'all' ||
      String(app.level_code || '').toUpperCase() === appLevelFilter.toUpperCase();
    const matchesStatus =
      appStatusFilter === 'all' ||
      String(app.status_code || '').toLowerCase() === appStatusFilter.toLowerCase();
    return matchesSearch && matchesLevel && matchesStatus;
  });

  const filteredPortalUsers = portalUsers.filter((pu) => {
    const q = appSearch.trim().toLowerCase();
    return (
      !q ||
      String(pu.full_name || '').toLowerCase().includes(q) ||
      String(pu.email || '').toLowerCase().includes(q) ||
      String(pu.phone || '').toLowerCase().includes(q)
    );
  });

  const filteredAuditEvents = auditEvents.filter((ev) => {
    const q = auditSearch.trim().toLowerCase();
    const matchesSearch =
      !q ||
      String(ev.actor_name || '').toLowerCase().includes(q) ||
      String(ev.actor_email || '').toLowerCase().includes(q) ||
      String(ev.note || '').toLowerCase().includes(q) ||
      String(ev.target_display || '').toLowerCase().includes(q);
    const matchesType = auditTypeFilter === 'all' || ev.event_type === auditTypeFilter;
    return matchesSearch && matchesType;
  });

  const appCountsByLevel = React.useMemo(() => {
    const counts = { PG: 0, TK: 0, SD: 0, SMP: 0, SMA: 0 };
    applications.forEach((a) => {
      const lvl = String(a.level_code || '').toUpperCase();
      if (counts[lvl] !== undefined) counts[lvl] += 1;
    });
    return counts;
  }, [applications]);

  const onlineCount = Math.max(1, sessionData.online_users?.length || sessionData.online_count || 1);

  // ============================= RENDER TABS =============================
  const renderDashboardOverview = () => (
    <div className="sa-portal-container">
      {/* Executive Hero Banner */}
      <div className="sa-hero-banner">
        <div className="sa-hero-left">
          <span className="sa-hero-kicker">
            <ShieldCheck size={14} />
            <span>Super Admin Executive Control Center</span>
          </span>
          <h1 className="sa-hero-title">
            Selamat Datang, {user?.full_name || 'Super Admin'}
          </h1>
          <p className="sa-hero-subtitle">
            Pusat kendali akun staff, pemantauan sesi aktif beserta alamat IP secara real-time, data pendaftar siswa baru (PPDB), serta pengawasan alur persetujuan konten website.
          </p>
        </div>

        <div className="sa-hero-right">
          <div className="sa-ip-pill" title="Alamat IP sesi Anda saat ini">
            <Globe size={14} />
            <span>IP Anda:</span>
            <strong>{sessionData.requester_ip || 'Terdeteksi Aktif'}</strong>
          </div>
          <button
            type="button"
            className="sa-btn sa-btn-glass"
            onClick={refreshAllData}
          >
            <RefreshCw size={15} />
            <span>Refresh Data</span>
          </button>
        </div>
      </div>

      {/* 4 Main KPI Cards */}
      <div className="sa-stats-grid">
        <div
          className="sa-stat-card clickable"
          onClick={() => setActiveTab('users')}
        >
          <div className="sa-stat-top">
            <div>
              <div className="sa-stat-label">Total Akun Staff</div>
              <div className="sa-stat-value">{usersLoading ? '...' : users.length}</div>
            </div>
            <div className="sa-stat-icon-box blue">
              <Users size={20} />
            </div>
          </div>
          <div className="sa-stat-footer">
            <span>
              <strong>{users.filter((u) => u.is_active).length}</strong> Aktif •{' '}
              <strong>{users.filter((u) => !u.is_active).length}</strong> Nonaktif
            </span>
            <ChevronRight size={15} />
          </div>
        </div>

        <div
          className="sa-stat-card clickable"
          onClick={() => setActiveTab('active-users')}
        >
          <div className="sa-stat-top">
            <div>
              <div className="sa-stat-label">User Sedang Aktif (Online)</div>
              <div className="sa-stat-value" style={{ color: '#059669' }}>
                {onlineCount}
              </div>
            </div>
            <div className="sa-stat-icon-box green">
              <Activity size={20} />
            </div>
          </div>
          <div className="sa-stat-footer">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#065f46', fontWeight: 600 }}>
              <span className="sa-live-dot" />
              Live IP & Perangkat Terpantau
            </span>
            <ChevronRight size={15} />
          </div>
        </div>

        <div
          className="sa-stat-card clickable"
          onClick={() => setActiveTab('applicants')}
        >
          <div className="sa-stat-top">
            <div>
              <div className="sa-stat-label">Data Pendaftar (PPDB)</div>
              <div className="sa-stat-value">
                {applicantsLoading ? '...' : applications.length}
              </div>
            </div>
            <div className="sa-stat-icon-box teal">
              <GraduationCap size={20} />
            </div>
          </div>
          <div className="sa-stat-footer">
            <span>
              <strong>{portalUsers.length}</strong> Akun Orang Tua Terdaftar
            </span>
            <ChevronRight size={15} />
          </div>
        </div>

        <div
          className="sa-stat-card clickable"
          onClick={() => setActiveTab('submissions-monitor')}
        >
          <div className="sa-stat-top">
            <div>
              <div className="sa-stat-label">Antrean Approval Konten</div>
              <div className="sa-stat-value" style={{ color: cmsCounts.pending > 0 ? '#d97706' : '#0f172a' }}>
                {cmsCounts.pending}
              </div>
            </div>
            <div className="sa-stat-icon-box amber">
              <FileCheck size={20} />
            </div>
          </div>
          <div className="sa-stat-footer">
            <span>
              <strong>{cmsCounts.approved}</strong> Disetujui •{' '}
              <strong>{cmsCounts.rejected}</strong> Ditolak
            </span>
            <ChevronRight size={15} />
          </div>
        </div>
      </div>

      {/* 2-Column Live Overview Panels */}
      <div className="sa-two-col-grid">
        {/* Left Panel: Live Active Users & IP */}
        <div className="sa-card">
          <div className="sa-card-header">
            <h3 className="sa-card-title">
              <span className="sa-live-dot" />
              <span>User yang Sedang Aktif & IP Address</span>
            </h3>
            <button
              type="button"
              className="sa-btn sa-btn-secondary sa-btn-sm"
              onClick={() => setActiveTab('active-users')}
            >
              <span>Lihat Semua Sesi</span>
              <ChevronRight size={14} />
            </button>
          </div>
          <div className="sa-card-body" style={{ padding: 0 }}>
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>IP Address & Perangkat</th>
                    <th>Aktif Terakhir</th>
                  </tr>
                </thead>
                <tbody>
                  {(sessionData.online_users || []).slice(0, 5).map((s) => {
                    const ua = parseUserAgentInfo(s.user_agent);
                    return (
                      <tr key={s.id}>
                        <td>
                          <div className="sa-user-cell">
                            <div className="sa-avatar">
                              {String(s.full_name || 'U').charAt(0).toUpperCase()}
                              <span className="sa-avatar-status online" />
                            </div>
                            <div className="sa-user-meta">
                              <span className="sa-user-name">
                                {s.full_name}
                                {s.is_current_session && (
                                  <span className="sa-badge sa-badge-online" style={{ fontSize: '0.65rem', padding: '1px 6px' }}>
                                    Anda
                                  </span>
                                )}
                              </span>
                              <span className="sa-user-email">{s.email}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {(s.role_codes || []).map((rc) => {
                              const rb = getRoleBadgeInfo(rc);
                              return (
                                <span key={rc} className={`sa-badge ${rb.cls}`}>
                                  {rb.label}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <span className="sa-ip-code">
                              <Globe size={12} />
                              {s.ip_address || '-'}
                            </span>
                            <span style={{ fontSize: '0.73rem', color: '#64748b' }}>
                              {ua.browser} • {ua.os}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="sa-badge sa-badge-online">
                            {formatRelativeTime(s.last_active_at, s.seconds_since_active)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {(!sessionData.online_users || sessionData.online_users.length === 0) && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: '28px', color: '#64748b' }}>
                        Belum ada data sesi aktif.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Panel: Recent Applicants & Audit Activity */}
        <div className="sa-card">
          <div className="sa-card-header">
            <h3 className="sa-card-title">
              <GraduationCap size={17} style={{ color: '#0d9488' }} />
              <span>Pendaftar Siswa Terbaru (PPDB)</span>
            </h3>
            <button
              type="button"
              className="sa-btn sa-btn-secondary sa-btn-sm"
              onClick={() => setActiveTab('applicants')}
            >
              <span>Kelola Pendaftar</span>
              <ChevronRight size={14} />
            </button>
          </div>
          <div className="sa-card-body" style={{ padding: 0 }}>
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Calon Siswa</th>
                    <th>Jenjang</th>
                    <th>Orang Tua / Kontak</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.slice(0, 5).map((app) => {
                    const st = getAdmissionStatusBadge(app.status_code);
                    return (
                      <tr key={app.id}>
                        <td>
                          <div className="sa-user-meta">
                            <span className="sa-user-name">{app.student_name}</span>
                            <span className="sa-user-email">
                              Daftar: {formatDateTime(app.created_at)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="sa-badge sa-badge-role-admin">
                            {String(app.level_code || '-').toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div className="sa-user-meta">
                            <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>{app.parent_name}</span>
                            <span className="sa-user-email">{app.phone !== '-' ? app.phone : app.applicant_email}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`sa-badge ${st.cls}`}>{st.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {applications.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: '28px', color: '#64748b' }}>
                        {portalUsers.length > 0
                          ? `Terdapat ${portalUsers.length} akun orang tua terdaftar. Klik "Kelola Pendaftar" untuk melihat detail.`
                          : 'Belum ada formulir pendaftaran siswa yang masuk.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUserManagementTab = () => (
    <div className="sa-portal-container">
      <div className="sa-section-header">
        <div className="sa-section-title-group">
          <h2 className="sa-section-title">
            <Users size={22} style={{ color: '#2563eb' }} />
            <span>Kelola User & Hak Akses Staff</span>
          </h2>
          <p className="sa-section-subtitle">
            Tambah akun staff baru, atur hak akses role (Website Admin, Principal, Admissions, Super Admin), reset password, atau nonaktifkan akun.
          </p>
        </div>
        <div className="sa-section-actions">
          <button
            type="button"
            className="sa-btn sa-btn-secondary"
            onClick={loadUsers}
          >
            <RefreshCw size={15} />
            <span>Refresh</span>
          </button>
          <button
            type="button"
            className="sa-btn sa-btn-primary"
            onClick={openCreateUserModal}
          >
            <UserPlus size={16} />
            <span>Tambah User Baru</span>
          </button>
        </div>
      </div>

      {/* Role Filter Pills */}
      <div className="sa-subtabs">
        {[
          { key: 'all', label: 'Semua User', count: users.length },
          {
            key: 'super_admin',
            label: 'Super Admin',
            count: users.filter((u) => (u.role_codes || []).includes('super_admin')).length
          },
          {
            key: 'admin',
            label: 'Website Admin (CMS)',
            count: users.filter((u) => (u.role_codes || []).includes('admin') || (u.role_codes || []).includes('website_admin')).length
          },
          {
            key: 'approver',
            label: 'Principal (Approver)',
            count: users.filter((u) => (u.role_codes || []).includes('approver') || (u.role_codes || []).includes('principal')).length
          },
          {
            key: 'admissions',
            label: 'Admin Admissions',
            count: users.filter((u) => (u.role_codes || []).includes('admissions')).length
          }
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`sa-subtab-btn ${userRoleFilter === tab.key ? 'active' : ''}`}
            onClick={() => setUserRoleFilter(tab.key)}
          >
            <span>{tab.label}</span>
            <span className="sa-subtab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      <div className="sa-card">
        <div className="sa-toolbar">
          <div className="sa-search-box">
            <Search size={16} className="sa-search-icon" />
            <input
              type="text"
              className="sa-search-input"
              placeholder="Cari nama lengkap atau alamat email user..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          </div>
          <div className="sa-filter-group">
            <select
              className="sa-select"
              value={userStatusFilter}
              onChange={(e) => setUserStatusFilter(e.target.value)}
            >
              <option value="all">Semua Status Akun</option>
              <option value="active">Status: Aktif</option>
              <option value="inactive">Status: Nonaktif</option>
            </select>
          </div>
        </div>

        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Nama & Email</th>
                <th>Role / Kewenangan</th>
                <th>Status Akun</th>
                <th>Status Sesi & IP Terakhir</th>
                <th>Terdaftar Pada</th>
                <th style={{ textAlign: 'right' }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {usersLoading ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    Memuat data akun user...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    Tidak ada user yang sesuai dengan filter pencarian.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => {
                  const sess = sessionMapByUserId[Number(u.id)];
                  const isOnline = Boolean(u.is_online || sess?.is_online);
                  const lastIp = u.last_ip || sess?.ip_address || '-';
                  const lastActive = u.last_active_at || sess?.last_active_at || u.last_login_at;
                  const isSelf = user && Number(u.id) === Number(user.id);

                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="sa-user-cell">
                          <div className="sa-avatar">
                            {String(u.full_name || 'U').charAt(0).toUpperCase()}
                            <span className={`sa-avatar-status ${isOnline ? 'online' : 'offline'}`} />
                          </div>
                          <div className="sa-user-meta">
                            <span className="sa-user-name">
                              {u.full_name}
                              {isSelf && (
                                <span className="sa-badge sa-badge-online" style={{ fontSize: '0.65rem', padding: '1px 6px' }}>
                                  Akun Anda
                                </span>
                              )}
                            </span>
                            <span className="sa-user-email">{u.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {(u.role_codes || []).map((rc) => {
                            const rb = getRoleBadgeInfo(rc);
                            return (
                              <span key={rc} className={`sa-badge ${rb.cls}`}>
                                {rb.label}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td>
                        <span className={`sa-badge ${u.is_active ? 'sa-badge-active' : 'sa-badge-inactive'}`}>
                          {u.is_active ? 'Aktif' : 'Nonaktif'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span className={isOnline ? 'sa-live-dot' : 'sa-offline-dot'} />
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: isOnline ? '#065f46' : '#64748b' }}>
                              {isOnline ? 'Sedang Online' : 'Offline'}
                            </span>
                            {lastIp && lastIp !== '-' && (
                              <span className="sa-ip-code">{lastIp}</span>
                            )}
                          </div>
                          {lastActive && (
                            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                              Aktivitas: {formatRelativeTime(lastActive)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{formatDateTime(u.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                          <button
                            type="button"
                            className="sa-btn sa-btn-secondary sa-btn-sm"
                            onClick={() => openEditUserModal(u)}
                            title="Edit user atau reset password"
                          >
                            <Edit3 size={13} />
                            <span>Edit</span>
                          </button>
                          {!isSelf && (
                            <button
                              type="button"
                              className="sa-btn sa-btn-secondary sa-btn-sm"
                              onClick={() => handleQuickToggleUserActive(u)}
                              title={u.is_active ? 'Nonaktifkan akun' : 'Aktifkan akun'}
                            >
                              {u.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                              <span>{u.is_active ? 'Nonaktifkan' : 'Aktifkan'}</span>
                            </button>
                          )}
                          {!isSelf && (
                            <button
                              type="button"
                              className="sa-btn sa-btn-danger sa-btn-sm"
                              onClick={() => handleDeleteUser(u)}
                              title="Hapus akun user"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderActiveUsersTab = () => (
    <div className="sa-portal-container">
      <div className="sa-section-header">
        <div className="sa-section-title-group">
          <h2 className="sa-section-title">
            <Activity size={22} style={{ color: '#059669' }} />
            <span>Monitor User Aktif & Alamat IP (Live Session Tracker)</span>
          </h2>
          <p className="sa-section-subtitle">
            Pantau siapa saja staff yang sedang login saat ini beserta alamat IP, browser/OS perangkat, waktu login, dan riwayat aksesnya.
          </p>
        </div>
        <div className="sa-section-actions">
          <div className="sa-badge sa-badge-online" style={{ padding: '7px 12px', fontSize: '0.78rem' }}>
            <span className="sa-live-dot" />
            <span>Auto-Update Tiap 30 Detik</span>
          </div>
          <button
            type="button"
            className="sa-btn sa-btn-primary"
            onClick={() => loadActiveSessions(false)}
          >
            <RefreshCw size={15} />
            <span>Refresh Sesi Sekarang</span>
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="sa-subtabs">
        <button
          type="button"
          className={`sa-subtab-btn ${sessionSubTab === 'online' ? 'active' : ''}`}
          onClick={() => setSessionSubTab('online')}
        >
          <span className="sa-live-dot" />
          <span>Sedang Online Sekarang</span>
          <span className="sa-subtab-count">{sessionData.online_users?.length || 1}</span>
        </button>
        <button
          type="button"
          className={`sa-subtab-btn ${sessionSubTab === 'all-sessions' ? 'active' : ''}`}
          onClick={() => setSessionSubTab('all-sessions')}
        >
          <Monitor size={15} />
          <span>Status Sesi & IP Seluruh User</span>
          <span className="sa-subtab-count">{sessionData.all_user_sessions?.length || users.length}</span>
        </button>
        <button
          type="button"
          className={`sa-subtab-btn ${sessionSubTab === 'login-history' ? 'active' : ''}`}
          onClick={() => setSessionSubTab('login-history')}
        >
          <Clock size={15} />
          <span>Riwayat Login & Logout</span>
          <span className="sa-subtab-count">{sessionData.recent_login_logs?.length || 0}</span>
        </button>
      </div>

      {sessionSubTab === 'online' && (
        <div className="sa-sessions-grid">
          {(sessionData.online_users || []).map((s) => {
            const ua = parseUserAgentInfo(s.user_agent);
            const DeviceIcon = ua.isMobile ? Smartphone : Monitor;
            return (
              <div key={s.id} className="sa-session-card">
                <div className="sa-session-card-top">
                  <div className="sa-user-cell">
                    <div className="sa-avatar">
                      {String(s.full_name || 'U').charAt(0).toUpperCase()}
                      <span className="sa-avatar-status online" />
                    </div>
                    <div className="sa-user-meta">
                      <span className="sa-user-name">{s.full_name}</span>
                      <span className="sa-user-email">{s.email}</span>
                    </div>
                  </div>
                  <span className="sa-badge sa-badge-online">
                    <span className="sa-live-dot" />
                    <span>{s.is_current_session ? 'ONLINE (ANDA)' : 'ONLINE'}</span>
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {(s.role_codes || []).map((rc) => {
                    const rb = getRoleBadgeInfo(rc);
                    return (
                      <span key={rc} className={`sa-badge ${rb.cls}`}>
                        {rb.label}
                      </span>
                    );
                  })}
                </div>

                <div className="sa-session-meta-grid">
                  <div className="sa-session-meta-item">
                    <span className="sa-session-meta-label">Alamat IP (IP Address)</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                      <span className="sa-ip-code">
                        <Globe size={12} />
                        {s.ip_address || '-'}
                      </span>
                      {s.ip_address && s.ip_address !== '-' && (
                        <button
                          type="button"
                          onClick={() => handleCopyIp(s.ip_address)}
                          title="Salin IP Address"
                          style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            color: copiedIp === s.ip_address ? '#059669' : '#64748b',
                            padding: '2px'
                          }}
                        >
                          {copiedIp === s.ip_address ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="sa-session-meta-item">
                    <span className="sa-session-meta-label">Browser & Sistem Operasi</span>
                    <span className="sa-session-meta-val" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <DeviceIcon size={13} style={{ color: '#2563eb', flexShrink: 0 }} />
                      <span>{ua.browser} • {ua.os}</span>
                    </span>
                  </div>

                  <div className="sa-session-meta-item">
                    <span className="sa-session-meta-label">Waktu Login</span>
                    <span className="sa-session-meta-val">{formatDateTime(s.login_at)}</span>
                  </div>

                  <div className="sa-session-meta-item">
                    <span className="sa-session-meta-label">Aktivitas Terakhir</span>
                    <span className="sa-session-meta-val" style={{ color: '#059669' }}>
                      {formatRelativeTime(s.last_active_at, s.seconds_since_active)}
                    </span>
                  </div>
                </div>

                {!s.is_current_session && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="sa-btn sa-btn-danger sa-btn-sm"
                      onClick={() => handleRevokeSession(s)}
                    >
                      <LogOut size={13} />
                      <span>Akhiri Sesi User Ini</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {sessionSubTab === 'all-sessions' && (
        <div className="sa-card">
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>User Staff</th>
                  <th>Role</th>
                  <th>Status Sesi</th>
                  <th>IP Address Terakhir</th>
                  <th>Perangkat / Browser</th>
                  <th>Login Terakhir</th>
                  <th>Heartbeat Terakhir</th>
                  <th style={{ textAlign: 'right' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {(sessionData.all_user_sessions || []).map((s) => {
                  const ua = parseUserAgentInfo(s.user_agent);
                  return (
                    <tr key={s.id}>
                      <td>
                        <div className="sa-user-cell">
                          <div className="sa-avatar">
                            {String(s.full_name || 'U').charAt(0).toUpperCase()}
                            <span className={`sa-avatar-status ${s.is_online ? 'online' : 'offline'}`} />
                          </div>
                          <div className="sa-user-meta">
                            <span className="sa-user-name">{s.full_name}</span>
                            <span className="sa-user-email">{s.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {(s.role_codes || []).map((rc) => {
                            const rb = getRoleBadgeInfo(rc);
                            return (
                              <span key={rc} className={`sa-badge ${rb.cls}`}>
                                {rb.label}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td>
                        <span className={`sa-badge ${s.is_online ? 'sa-badge-online' : 'sa-badge-offline'}`}>
                          <span className={s.is_online ? 'sa-live-dot' : 'sa-offline-dot'} />
                          <span>{s.is_online ? 'Online' : 'Offline'}</span>
                        </span>
                      </td>
                      <td>
                        <span className="sa-ip-code">
                          <Globe size={12} />
                          {s.ip_address || '-'}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.8rem', color: '#334155' }}>
                          {s.user_agent ? `${ua.browser} (${ua.os})` : '-'}
                        </span>
                      </td>
                      <td>{formatDateTime(s.login_at)}</td>
                      <td>{formatRelativeTime(s.last_active_at, s.seconds_since_active)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {s.is_online && !s.is_current_session ? (
                          <button
                            type="button"
                            className="sa-btn sa-btn-danger sa-btn-sm"
                            onClick={() => handleRevokeSession(s)}
                          >
                            <LogOut size={13} />
                            <span>Force Logout</span>
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sessionSubTab === 'login-history' && (
        <div className="sa-card">
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Waktu</th>
                  <th>User</th>
                  <th>Aktivitas Sesi</th>
                  <th>Keterangan & IP Address</th>
                </tr>
              </thead>
              <tbody>
                {(sessionData.recent_login_logs || []).length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '36px', color: '#64748b' }}>
                      Belum ada riwayat log login/logout.
                    </td>
                  </tr>
                ) : (
                  (sessionData.recent_login_logs || []).map((lg) => (
                    <tr key={lg.id}>
                      <td>{formatDateTime(lg.created_at)}</td>
                      <td>
                        <div className="sa-user-meta">
                          <span className="sa-user-name">{lg.actor_name || '-'}</span>
                          <span className="sa-user-email">{lg.actor_email || ''}</span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`sa-badge ${
                            lg.event_type === 'login' ? 'sa-badge-online' : 'sa-badge-offline'
                          }`}
                        >
                          {lg.event_type === 'login'
                            ? 'LOGIN MASUK'
                            : lg.event_type === 'force_logout'
                              ? 'FORCE LOGOUT'
                              : 'LOGOUT KELUAR'}
                        </span>
                      </td>
                      <td>{lg.note || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderApplicantsTab = () => (
    <div className="sa-portal-container">
      <div className="sa-section-header">
        <div className="sa-section-title-group">
          <h2 className="sa-section-title">
            <GraduationCap size={22} style={{ color: '#0d9488' }} />
            <span>Data Pendaftar Siswa Baru (PPDB) & Akun Orang Tua</span>
          </h2>
          <p className="sa-section-subtitle">
            Lihat seluruh data calon siswa yang mendaftar, rincian formulir per jenjang (PG, TK, SD, SMP, SMA), kontak orang tua, serta export laporan ke Excel/CSV.
          </p>
        </div>
        <div className="sa-section-actions">
          <button
            type="button"
            className="sa-btn sa-btn-secondary"
            onClick={loadApplicantsData}
          >
            <RefreshCw size={15} />
            <span>Refresh Data</span>
          </button>
          <button
            type="button"
            className="sa-btn sa-btn-primary"
            onClick={() => {
              if (activeTab !== 'portal-users') {
                exportRowsToCsv(
                  `data-pendaftar-ppdb-${new Date().toISOString().slice(0, 10)}.csv`,
                  ['ID', 'Nama Siswa', 'Tanggal Lahir', 'Jenjang', 'Nama Orang Tua', 'Email Orang Tua', 'No Telepon / WA', 'Status', 'Catatan Admin', 'Tanggal Daftar'],
                  filteredApplications.map((a) => [
                    a.id,
                    a.student_name,
                    a.birth_date,
                    a.level_code,
                    a.parent_name,
                    a.applicant_email,
                    a.phone,
                    a.status_code,
                    a.admin_note || '',
                    a.created_at
                  ])
                );
              } else {
                exportRowsToCsv(
                  `akun-orang-tua-ppdb-${new Date().toISOString().slice(0, 10)}.csv`,
                  ['ID', 'Nama Lengkap Orang Tua', 'Email', 'No WhatsApp / Telepon', 'Jumlah Anak Didaftarkan', 'Jenjang Diambil', 'Tanggal Registrasi'],
                  filteredPortalUsers.map((pu) => [
                    pu.id,
                    pu.full_name,
                    pu.email,
                    pu.phone,
                    pu.applications_count,
                    pu.applied_levels,
                    pu.created_at
                  ])
                );
              }
            }}
          >
            <Download size={15} />
            <span>Export CSV / Excel</span>
          </button>
        </div>
      </div>

      {/* Summary Level Cards */}
      <div className="sa-stats-grid">
        <div className="sa-stat-card">
          <div className="sa-stat-top">
            <div>
              <div className="sa-stat-label">Total Formulir Siswa</div>
              <div className="sa-stat-value">{applications.length}</div>
            </div>
            <div className="sa-stat-icon-box blue">
              <FileText size={20} />
            </div>
          </div>
          <div className="sa-stat-footer">
            <span>
              PG: <strong>{appCountsByLevel.PG}</strong> • TK: <strong>{appCountsByLevel.TK}</strong> • SD: <strong>{appCountsByLevel.SD}</strong> • SMP: <strong>{appCountsByLevel.SMP}</strong> • SMA: <strong>{appCountsByLevel.SMA}</strong>
            </span>
          </div>
        </div>

        <div className="sa-stat-card">
          <div className="sa-stat-top">
            <div>
              <div className="sa-stat-label">Menunggu Review</div>
              <div className="sa-stat-value" style={{ color: '#2563eb' }}>
                {applications.filter((a) => a.status_code === 'submitted' || a.status_code === 'in_review').length}
              </div>
            </div>
            <div className="sa-stat-icon-box amber">
              <Clock size={20} />
            </div>
          </div>
          <div className="sa-stat-footer">
            <span>Status Submitted / In Review</span>
          </div>
        </div>

        <div className="sa-stat-card">
          <div className="sa-stat-top">
            <div>
              <div className="sa-stat-label">Disetujui / Diterima</div>
              <div className="sa-stat-value" style={{ color: '#059669' }}>
                {applications.filter((a) => a.status_code === 'approved').length}
              </div>
            </div>
            <div className="sa-stat-icon-box green">
              <CheckCircle2 size={20} />
            </div>
          </div>
          <div className="sa-stat-footer">
            <span>
              Ditolak / Revisi:{' '}
              <strong>
                {applications.filter((a) => a.status_code === 'rejected' || a.status_code === 'revision_required').length}
              </strong>
            </span>
          </div>
        </div>

        <div className="sa-stat-card">
          <div className="sa-stat-top">
            <div>
              <div className="sa-stat-label">Akun Orang Tua Terdaftar</div>
              <div className="sa-stat-value" style={{ color: '#7c3aed' }}>
                {portalUsers.length}
              </div>
            </div>
            <div className="sa-stat-icon-box purple">
              <Users size={20} />
            </div>
          </div>
          <div className="sa-stat-footer">
            <span>Registrasi via Portal PPDB Online</span>
          </div>
        </div>
      </div>

      <div className="sa-card">
        <div className="sa-toolbar">
          <div className="sa-search-box">
            <Search size={16} className="sa-search-icon" />
            <input
              type="text"
              className="sa-search-input"
              placeholder="Cari nama siswa, nama orang tua, email, atau nomor WhatsApp..."
              value={appSearch}
              onChange={(e) => setAppSearch(e.target.value)}
            />
          </div>
          {activeTab !== 'portal-users' && (
            <div className="sa-filter-group">
              <select
                className="sa-select"
                value={appLevelFilter}
                onChange={(e) => setAppLevelFilter(e.target.value)}
              >
                <option value="all">Semua Jenjang</option>
                <option value="PG">Playgroup (PG)</option>
                <option value="TK">Kindergarten (TK)</option>
                <option value="SD">Primary School (SD)</option>
                <option value="SMP">Secondary School (SMP)</option>
                <option value="SMA">Senior High (SMA)</option>
              </select>

              <select
                className="sa-select"
                value={appStatusFilter}
                onChange={(e) => setAppStatusFilter(e.target.value)}
              >
                <option value="all">Semua Status</option>
                <option value="submitted">Submitted (Baru)</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved (Diterima)</option>
                <option value="revision_required">Perlu Revisi</option>
                <option value="rejected">Rejected (Ditolak)</option>
              </select>
            </div>
          )}
        </div>

        {activeTab !== 'portal-users' ? (
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nama Calon Siswa</th>
                  <th>Jenjang</th>
                  <th>Orang Tua / Wali</th>
                  <th>Kontak (Email & WA)</th>
                  <th>Status</th>
                  <th>Tanggal Daftar</th>
                  <th style={{ textAlign: 'right' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {applicantsLoading ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Memuat data pendaftar...
                    </td>
                  </tr>
                ) : filteredApplications.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Tidak ada formulir pendaftaran siswa yang ditemukan.
                    </td>
                  </tr>
                ) : (
                  filteredApplications.map((app) => {
                    const st = getAdmissionStatusBadge(app.status_code);
                    return (
                      <tr key={app.id}>
                        <td style={{ fontWeight: 700, color: '#64748b' }}>#{app.id}</td>
                        <td>
                          <div className="sa-user-meta">
                            <span className="sa-user-name">{app.student_name}</span>
                            {app.birth_date && app.birth_date !== '-' && (
                              <span className="sa-user-email">Lahir: {app.birth_date}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="sa-badge sa-badge-role-admin">
                            {getLevelDisplayName(app.level_code)}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontWeight: 600 }}>{app.parent_name}</span>
                        </td>
                        <td>
                          <div className="sa-user-meta">
                            <span className="sa-user-email">{app.applicant_email}</span>
                            {app.phone && app.phone !== '-' && (
                              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0d9488' }}>
                                {app.phone}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`sa-badge ${st.cls}`}>{st.label}</span>
                        </td>
                        <td>{formatDateTime(app.created_at)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            className="sa-btn sa-btn-secondary sa-btn-sm"
                            onClick={() => openApplicationDetailModal(app)}
                          >
                            <Eye size={13} />
                            <span>Detail & Status</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nama Lengkap Orang Tua</th>
                  <th>Email Login</th>
                  <th>Nomor WhatsApp / Telepon</th>
                  <th>Jumlah Anak Didaftarkan</th>
                  <th>Jenjang Diambil</th>
                  <th>Status Akun</th>
                  <th>Tanggal Registrasi</th>
                </tr>
              </thead>
              <tbody>
                {applicantsLoading ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Memuat data akun orang tua...
                    </td>
                  </tr>
                ) : filteredPortalUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Belum ada akun orang tua terdaftar.
                    </td>
                  </tr>
                ) : (
                  filteredPortalUsers.map((pu) => (
                    <tr key={pu.id}>
                      <td style={{ fontWeight: 700, color: '#64748b' }}>#{pu.id}</td>
                      <td>
                        <span className="sa-user-name">{pu.full_name}</span>
                      </td>
                      <td>{pu.email}</td>
                      <td>
                        <span style={{ fontWeight: 600, color: '#0f766e' }}>{pu.phone || '-'}</span>
                      </td>
                      <td>
                        <span className="sa-badge sa-badge-role-admin">
                          {pu.applications_count} Formulir Anak
                        </span>
                      </td>
                      <td>{pu.applied_levels || '-'}</td>
                      <td>
                        <span className={`sa-badge ${pu.is_active ? 'sa-badge-active' : 'sa-badge-inactive'}`}>
                          {pu.is_active ? 'Aktif' : 'Nonaktif'}
                        </span>
                      </td>
                      <td>{formatDateTime(pu.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderAuditLogsTab = () => (
    <div className="sa-portal-container">
      <div className="sa-section-header">
        <div className="sa-section-title-group">
          <h2 className="sa-section-title">
            <ShieldCheck size={22} style={{ color: '#7c3aed' }} />
            <span>Audit Keamanan & Riwayat Aktivitas Sistem</span>
          </h2>
          <p className="sa-section-subtitle">
            Rekam jejak seluruh aktivitas sensitif: pembuatan/penghapusan akun, perubahan role, riwayat sesi login beserta IP, dan log persetujuan konten.
          </p>
        </div>
        <div className="sa-section-actions">
          <button
            type="button"
            className="sa-btn sa-btn-secondary"
            onClick={loadSystemLogsAndCms}
          >
            <RefreshCw size={15} />
            <span>Refresh Log</span>
          </button>
          <button
            type="button"
            className="sa-btn sa-btn-primary"
            onClick={() => {
              exportRowsToCsv(
                `audit-log-sistem-${new Date().toISOString().slice(0, 10)}.csv`,
                ['ID', 'Waktu', 'Jenis Event', 'Pelaku (Actor)', 'Email Pelaku', 'Target', 'Keterangan / IP'],
                filteredAuditEvents.map((ev) => [
                  ev.id,
                  ev.created_at,
                  ev.event_type,
                  ev.actor_name || '-',
                  ev.actor_email || '-',
                  ev.target_display || ev.target_type || '-',
                  ev.note || '-'
                ])
              );
            }}
          >
            <Download size={15} />
            <span>Export Audit CSV</span>
          </button>
        </div>
      </div>

      <div className="sa-subtabs">
        <button
          type="button"
          className={`sa-subtab-btn ${auditSubTab === 'security' ? 'active' : ''}`}
          onClick={() => setAuditSubTab('security')}
        >
          <ShieldCheck size={15} />
          <span>Log Keamanan, Akses & Login</span>
          <span className="sa-subtab-count">{auditEvents.length}</span>
        </button>
        <button
          type="button"
          className={`sa-subtab-btn ${auditSubTab === 'content' ? 'active' : ''}`}
          onClick={() => setAuditSubTab('content')}
        >
          <FileCheck size={15} />
          <span>Log Perubahan Konten Legacy</span>
          <span className="sa-subtab-count">{contentHistory.length}</span>
        </button>
      </div>

      {auditSubTab === 'security' ? (
        <div className="sa-card">
          <div className="sa-toolbar">
            <div className="sa-search-box">
              <Search size={16} className="sa-search-icon" />
              <input
                type="text"
                className="sa-search-input"
                placeholder="Cari nama user, jenis aksi, atau alamat IP..."
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
              />
            </div>
            <div className="sa-filter-group">
              <select
                className="sa-select"
                value={auditTypeFilter}
                onChange={(e) => setAuditTypeFilter(e.target.value)}
              >
                <option value="all">Semua Jenis Aktivitas</option>
                <option value="login">Login Masuk</option>
                <option value="logout">Logout Keluar</option>
                <option value="force_logout">Force Logout</option>
                <option value="create_user">Pembuatan User Baru</option>
                <option value="update_access">Perubahan Hak Akses / Role</option>
                <option value="delete_user">Penghapusan User</option>
              </select>
            </div>
          </div>

          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Waktu</th>
                  <th>Pelaku (Actor)</th>
                  <th>Jenis Event</th>
                  <th>Target</th>
                  <th>Keterangan Detail / IP</th>
                </tr>
              </thead>
              <tbody>
                {auditLoading ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Memuat log keamanan sistem...
                    </td>
                  </tr>
                ) : filteredAuditEvents.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Tidak ada log aktivitas yang ditemukan.
                    </td>
                  </tr>
                ) : (
                  filteredAuditEvents.map((ev) => (
                    <tr key={ev.id}>
                      <td>{formatDateTime(ev.created_at)}</td>
                      <td>
                        <div className="sa-user-meta">
                          <span className="sa-user-name">{ev.actor_name || 'System'}</span>
                          <span className="sa-user-email">{ev.actor_email || ''}</span>
                        </div>
                      </td>
                      <td>
                        <span className="sa-badge sa-badge-role-admin">
                          {String(ev.event_type || '').toUpperCase()}
                        </span>
                      </td>
                      <td>{ev.target_display || ev.target_type || '-'}</td>
                      <td>{ev.note || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="sa-card">
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Waktu</th>
                  <th>Judul Konten</th>
                  <th>Aksi</th>
                  <th>Status</th>
                  <th>Oleh</th>
                  <th>Catatan</th>
                </tr>
              </thead>
              <tbody>
                {contentHistory.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Untuk riwayat perubahan field CMS terbaru secara lengkap, gunakan menu "Submissions" di sidebar.
                    </td>
                  </tr>
                ) : (
                  contentHistory.map((ch) => (
                    <tr key={ch.id}>
                      <td>{formatDateTime(ch.edited_at)}</td>
                      <td style={{ fontWeight: 600 }}>{ch.content_title || '-'}</td>
                      <td>{ch.action || '-'}</td>
                      <td>
                        <span className="sa-badge sa-badge-offline">{ch.status_code || '-'}</span>
                      </td>
                      <td>{ch.actor_name || '-'}</td>
                      <td>{ch.note || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderSignatoriesTab = () => (
    <div className="sa-portal-container">
      <div className="sa-section-header">
        <div className="sa-section-title-group">
          <h2 className="sa-section-title">
            <Award size={22} style={{ color: '#2563eb' }} />
            <span>Master Pejabat Penandatangan Dokumen Resmi (Signatories)</span>
          </h2>
          <p className="sa-section-subtitle">
            Konfigurasi nama dan gelar resmi Kepala Sekolah (School Principal) per jenjang serta Pejabat Keuangan (Finance PIC) yang otomatis tercetak pada Letter of Acceptance (LoA), Bukti Siswa Resmi (Certificate of Enrollment), dan Kwitansi Pembayaran Resmi (Official Receipt).
          </p>
        </div>
        <div className="sa-section-actions">
          <button
            type="button"
            className="sa-btn sa-btn-secondary"
            onClick={loadSignatories}
            disabled={signatoriesLoading}
          >
            <RefreshCw size={15} className={signatoriesLoading ? 'sa-spin' : ''} />
            <span>Refresh Data</span>
          </button>
        </div>
      </div>

      <div className="sa-card" style={{ padding: 24 }}>
        {signatoriesLoading && signatoriesList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
            Memuat data pejabat penandatangan...
          </div>
        ) : (
          <form onSubmit={handleSaveSignatories}>
            {/* Bagian 1: Kepala Sekolah per Jenjang */}
            <div style={{ marginBottom: 28, background: '#f8fafc', padding: 22, borderRadius: 12, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ background: '#eff6ff', color: '#2563eb', padding: 8, borderRadius: 8 }}>
                  <Award size={20} />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1e3a8a' }}>
                    1. Pejabat Kepala Sekolah (School Principal) Tiap Jenjang
                  </h4>
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                    Nama di bawah ini akan otomatis tercantum pada Letter of Acceptance (LoA) &amp; Official Enrollment Certificate sesuai jenjang anak yang diterima.
                  </span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                {signatoriesList.map((sig, idx) => (
                  <div key={sig.level_code} style={{ background: '#ffffff', padding: 16, borderRadius: 10, border: '1px solid #cbd5e1' }}>
                    <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 10, fontSize: '0.95rem' }}>
                      Jenjang: {sig.level_name} ({sig.level_code.toUpperCase()})
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                        Nama Lengkap &amp; Gelar Kepala Sekolah *
                      </label>
                      <input
                        type="text"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                        value={sig.principal_name || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSignatoriesList((prev) => {
                            const updated = [...prev];
                            updated[idx] = { ...updated[idx], principal_name: val };
                            return updated;
                          });
                        }}
                        placeholder="Contoh: Asnah Roidah Yulianti, S.Si., M.Pd"
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                        Jabatan Resmi (English Title) *
                      </label>
                      <input
                        type="text"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                        value={sig.principal_title || 'School Principal'}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSignatoriesList((prev) => {
                            const updated = [...prev];
                            updated[idx] = { ...updated[idx], principal_title: val };
                            return updated;
                          });
                        }}
                        placeholder="School Principal"
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bagian 2: Pejabat Finansial / Yayasan */}
            <div style={{ marginBottom: 28, background: '#f8fafc', padding: 22, borderRadius: 12, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ background: '#ecfdf5', color: '#059669', padding: 8, borderRadius: 8 }}>
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#065f46' }}>
                    2. Pejabat Keuangan &amp; Administrasi (Finance &amp; Treasury PIC)
                  </h4>
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                    Pejabat yang ditunjuk untuk pengesahan Kwitansi Pembayaran Resmi (Official Receipt) dan tanda bukti pelunasan biaya sekolah.
                  </span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                <div style={{ background: '#ffffff', padding: 16, borderRadius: 10, border: '1px solid #cbd5e1' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                    Nama Lengkap &amp; Gelar Finance PIC *
                  </label>
                  <input
                    type="text"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                    value={globalFinanceName}
                    onChange={(e) => setGlobalFinanceName(e.target.value)}
                    placeholder="Contoh: Zahra, S.E."
                    required
                  />
                </div>
                <div style={{ background: '#ffffff', padding: 16, borderRadius: 10, border: '1px solid #cbd5e1' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                    Jabatan Resmi Finance PIC (English Title) *
                  </label>
                  <input
                    type="text"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                    value={globalFinanceTitle}
                    onChange={(e) => setGlobalFinanceTitle(e.target.value)}
                    placeholder="Head of Finance & Administration"
                    required
                  />
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <button
                type="submit"
                className="sa-btn sa-btn-primary"
                disabled={savingSignatories}
                style={{ padding: '12px 24px', fontSize: '0.95rem', display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <Check size={18} />
                <span>{savingSignatories ? 'Menyimpan...' : 'Simpan Konfigurasi Pejabat Penandatangan'}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <DashboardLayout
      user={user}
      onLogout={onLogout}
      userRole="super_admin"
      activeTab={activeTab}
      onTabChange={setActiveTab}
      pendingCount={cmsCounts.pending}
      onlineUsersCount={onlineCount}
      applicantsCount={applications.length}
      apiBase={apiBase}
    >
      {(activeTab === 'dashboard' || activeTab === 'overview') && renderDashboardOverview()}
      {activeTab === 'users' && renderUserManagementTab()}
      {activeTab === 'active-users' && renderActiveUsersTab()}
      {activeTab === 'admissions-overview' && (
        <AdmissionsExecutiveOverview apiBase={apiBase} userRole="super_admin" />
      )}
      {(activeTab === 'applicants' || activeTab === 'portal-users') && renderApplicantsTab()}
      {activeTab === 'signatories' && renderSignatoriesTab()}
      {activeTab === 'submissions-monitor' && (
        <ApprovalStatusHistory
          apiBase={apiBase}
          onRefreshStats={loadSystemLogsAndCms}
          viewerRole="super_admin"
        />
      )}
      {activeTab === 'audit-logs' && renderAuditLogsTab()}

      {/* MODAL: CREATE / EDIT USER */}
      {showUserModal &&
        ReactDOM.createPortal(
          <div className="sa-modal-backdrop" onClick={() => setShowUserModal(false)}>
            <div className="sa-modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="sa-modal-header">
                <h3 className="sa-modal-title">
                  {editingUser ? <Edit3 size={18} /> : <UserPlus size={18} />}
                  <span>{editingUser ? `Edit Akun User: ${editingUser.full_name}` : 'Tambah Akun Staff Baru'}</span>
                </h3>
                <button
                  type="button"
                  className="sa-modal-close"
                  onClick={() => setShowUserModal(false)}
                >
                  <X size={16} />
                </button>
              </div>

              <form
                onSubmit={handleSaveUser}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflow: 'hidden'
                }}
              >
                <div className="sa-modal-body" style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                  {userFormError && (
                    <div
                      style={{
                        padding: '12px 14px',
                        borderRadius: '8px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#991b1b',
                        fontSize: '0.825rem',
                        fontWeight: 600
                      }}
                    >
                      {userFormError}
                    </div>
                  )}

                  <div className="sa-form-group">
                    <label className="sa-form-label">Nama Lengkap Staff *</label>
                    <input
                      type="text"
                      className="sa-form-input"
                      placeholder="Contoh: Budi Santoso"
                      value={userForm.full_name}
                      onChange={(e) => setUserForm((prev) => ({ ...prev, full_name: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="sa-form-group">
                    <label className="sa-form-label">Alamat Email Login *</label>
                    <input
                      type="email"
                      className="sa-form-input"
                      placeholder="nama@permataharapan.sch.id"
                      value={userForm.email}
                      onChange={(e) => setUserForm((prev) => ({ ...prev, email: e.target.value }))}
                      disabled={Boolean(editingUser)}
                      required
                    />
                  </div>

                  <div className="sa-form-group">
                    <label className="sa-form-label">
                      {editingUser ? 'Reset Password (kosongkan jika tidak ingin mengubah)' : 'Password Login *'}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPasswordText ? 'text' : 'password'}
                        className="sa-form-input"
                        placeholder={editingUser ? 'Ketik password baru minimal 8 karakter...' : 'Minimal 8 karakter...'}
                        value={userForm.password}
                        onChange={(e) => setUserForm((prev) => ({ ...prev, password: e.target.value }))}
                        style={{ paddingRight: '40px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswordText(!showPasswordText)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#64748b'
                        }}
                      >
                        {showPasswordText ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="sa-form-group">
                    <label className="sa-form-label">Pilih Role / Kewenangan *</label>
                    <div className="sa-role-cards">
                      {ROLE_OPTIONS.map((ro) => {
                        const isSelected = userForm.role_code === ro.code;
                        return (
                          <div
                            key={ro.code}
                            className={`sa-role-option ${isSelected ? 'selected' : ''}`}
                            onClick={() => setUserForm((prev) => ({ ...prev, role_code: ro.code }))}
                          >
                            <div className="sa-role-option-title">
                              <span>{ro.label}</span>
                              {isSelected && <CheckCircle2 size={15} style={{ color: '#2563eb' }} />}
                            </div>
                            <span className="sa-role-option-desc">{ro.desc}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="sa-form-group">
                    <label className="sa-form-label">Status Akun</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                        type="button"
                        className={`sa-btn sa-btn-sm ${userForm.is_active ? 'sa-btn-success' : 'sa-btn-secondary'}`}
                        onClick={() => setUserForm((prev) => ({ ...prev, is_active: true }))}
                      >
                        <CheckCircle2 size={14} />
                        <span>Aktif (Dapat Login)</span>
                      </button>
                      <button
                        type="button"
                        className={`sa-btn sa-btn-sm ${!userForm.is_active ? 'sa-btn-danger' : 'sa-btn-secondary'}`}
                        onClick={() => setUserForm((prev) => ({ ...prev, is_active: false }))}
                      >
                        <XCircle size={14} />
                        <span>Nonaktif (Diblokir)</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="sa-modal-footer">
                  <button
                    type="button"
                    className="sa-btn sa-btn-secondary"
                    onClick={() => setShowUserModal(false)}
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="sa-btn sa-btn-primary"
                    disabled={userSubmitting}
                  >
                    {userSubmitting ? 'Menyimpan...' : editingUser ? 'Simpan Perubahan' : 'Buat Akun User'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* MODAL: APPLICANT DETAIL & STATUS UPDATE */}
      {selectedApplication &&
        ReactDOM.createPortal(
          <div className="sa-modal-backdrop" onClick={() => setSelectedApplication(null)}>
            <div className="sa-modal-dialog wide" onClick={(e) => e.stopPropagation()}>
              <div className="sa-modal-header">
                <h3 className="sa-modal-title">
                  <GraduationCap size={19} style={{ color: '#0d9488' }} />
                  <span>
                    Detail Pendaftaran Siswa #{selectedApplication.id} — {selectedApplication.student_name}
                  </span>
                </h3>
                <button
                  type="button"
                  className="sa-modal-close"
                  onClick={() => setSelectedApplication(null)}
                >
                  <X size={16} />
                </button>
              </div>

              <form
                onSubmit={handleUpdateApplicationStatus}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflow: 'hidden'
                }}
              >
                <div className="sa-modal-body" style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                  <div className="sa-detail-grid">
                    <div className="sa-detail-item">
                      <span className="sa-detail-label">Nama Lengkap Calon Siswa</span>
                      <span className="sa-detail-val">{selectedApplication.student_name}</span>
                    </div>
                    <div className="sa-detail-item">
                      <span className="sa-detail-label">Jenjang Pendidikan</span>
                      <span className="sa-detail-val">
                        {getLevelDisplayName(selectedApplication.level_code)}
                      </span>
                    </div>
                    <div className="sa-detail-item">
                      <span className="sa-detail-label">Tanggal Lahir Siswa</span>
                      <span className="sa-detail-val">{selectedApplication.birth_date || '-'}</span>
                    </div>
                    <div className="sa-detail-item">
                      <span className="sa-detail-label">Nama Orang Tua / Wali</span>
                      <span className="sa-detail-val">{selectedApplication.parent_name}</span>
                    </div>
                    <div className="sa-detail-item">
                      <span className="sa-detail-label">Email Akun Orang Tua</span>
                      <span className="sa-detail-val">{selectedApplication.applicant_email}</span>
                    </div>
                    <div className="sa-detail-item">
                      <span className="sa-detail-label">Nomor Telepon / WhatsApp</span>
                      <span className="sa-detail-val">{selectedApplication.phone || '-'}</span>
                    </div>
                    <div className="sa-detail-item">
                      <span className="sa-detail-label">Waktu Pendaftaran</span>
                      <span className="sa-detail-val">{formatDateTime(selectedApplication.created_at)}</span>
                    </div>
                  </div>

                  {/* Additional dynamic form fields */}
                  {selectedApplication.form_data &&
                    Object.keys(selectedApplication.form_data).filter(
                      (k) => !['student_full_name', 'student_name', 'birth_date', 'parent_name', 'phone'].includes(k)
                    ).length > 0 && (
                      <div className="sa-form-group">
                        <label className="sa-form-label">Informasi Tambahan Formulir Jenjang</label>
                        <div className="sa-detail-grid">
                          {Object.entries(selectedApplication.form_data)
                            .filter(
                              ([k]) =>
                                !['student_full_name', 'student_name', 'birth_date', 'parent_name', 'phone'].includes(k)
                            )
                            .map(([k, v]) => (
                              <div key={k} className="sa-detail-item">
                                <span className="sa-detail-label">{k.replace(/_/g, ' ')}</span>
                                <span className="sa-detail-val">{String(v || '-')}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  <div className="sa-form-group">
                    <label className="sa-form-label">Status Pendaftaran</label>
                    <select
                      className="sa-select"
                      value={appModalStatus}
                      onChange={(e) => setAppModalStatus(e.target.value)}
                    >
                      <option value="submitted">Submitted (Terdaftar Baru)</option>
                      <option value="in_review">In Review (Sedang Diseleksi)</option>
                      <option value="approved">Approved (Diterima / Lulus)</option>
                      <option value="revision_required">Revision Required (Perlu Perbaikan Data)</option>
                      <option value="rejected">Rejected (Tidak Diterima)</option>
                    </select>
                  </div>

                  <div className="sa-form-group">
                    <label className="sa-form-label">Catatan Admin / Keterangan Seleksi</label>
                    <textarea
                      rows={3}
                      className="sa-form-textarea"
                      placeholder="Tambahkan catatan untuk orang tua / arsip pendaftaran..."
                      value={appModalNote}
                      onChange={(e) => setAppModalNote(e.target.value)}
                    />
                  </div>
                </div>

                <div className="sa-modal-footer">
                  <button
                    type="button"
                    className="sa-btn sa-btn-secondary"
                    onClick={() => setSelectedApplication(null)}
                  >
                    Tutup
                  </button>
                  <button
                    type="submit"
                    className="sa-btn sa-btn-primary"
                    disabled={appUpdating}
                  >
                    {appUpdating ? 'Menyimpan...' : 'Simpan Status Pendaftar'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* TOAST NOTIFICATION */}
      {toast && (
        <div className={`sa-toast ${toast.type}`}>
          {toast.type === 'error' ? <XCircle size={17} /> : <CheckCircle2 size={17} />}
          <span>{toast.message}</span>
        </div>
      )}
    </DashboardLayout>
  );
}

export default SuperAdminDashboard;
